import React, {useEffect, useState} from 'react'
import {
    DESIGN_KEY_PREFIX, DESIGN_VARIANT_STATUS, DOWNLOAD_MULTI_IMAGE_TIME_DELAY, REPEAT_IMAGE_FIELD_LIST
} from "../../constants/design.constant";
import {getNextItemKey} from "../../../common/utils/item-key-util";
import SelectColorTypeDetails from "../design-editor/settings/select-color-type-details";
import RenderFields from "../../../common/ui/render-fields";
import _ from "lodash";
import {prepareImageFileName} from "../../utils/design-variant-util";
import SelectedParentProductsList from "./selected-parent-products-list";
import RenderSingleDesignVariant from "./render-single-design-variant";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import {updateDesignVariants} from "../../services/design-variant-data-service";
import {updateSingleDesign} from "../../services/design-data-service";
import {getImageDataUrl, resizeDataURL} from "../../../common/utils/image-util";
import {getExternalImageDownloadUrl} from "../../../common/services/cloud-function-service";
import ParentProductTemplates from "./parent-product-templates";

const ProductsDesigner = ({
                              showGrid,
                              designsMap,
                              setDesignsMap,
                              parentProduct,
                              processedImageList,
                              setSelectedParentProduct,
                              selectedParentProductList,
                              downloadAllImages,
                              setDownloadAllImages,
                              isBatchEdit = false,
                              batchDetail = undefined,
                              designVariantList = [],
                              setDesignVariantList = undefined,
                              setSaveDesignVariants = undefined,
                              saveDesignVariants = false,
                              existingDesignDetail = undefined,
                              setExistingDesignDetail = undefined,
                              containerCss = 'select-images-table-container',
                          }) => {
    const [initialStep, setInitialStep] = useState(true);
    const [commonSettings, setCommonSettings] = useState(null);
    const [showDesigner, setShowDesigner] = useState(false);
    const [selectedDesignVariant, setSelectedDesignVariant] = useState(null);
    const [imageIdsList, setImageIdsList] = useState([]);
    const [designDetail, setDesignDetail] = useState(null);
    const [templateImage, setTemplateImage] = useState(null);

    useEffect(() => {
        if (parentProduct) {
            setShowDesigner(false);
            initCommonSettings();
        }
    }, [parentProduct]);

    useEffect(() => {
        if (isBatchEdit && !_.isEmpty(designVariantList)) {
            setShowDesigner(false);
            initCommonSettings();
        }
    }, [isBatchEdit, designVariantList]);

    useEffect(() => {
        updateSettingsInDesign();
    }, [commonSettings]);

    useEffect(() => {
        if (saveDesignVariants) {
            updateExistingDesignVariants();
        }
    }, [saveDesignVariants]);

    const updateSettingsInDesign = () => {
        if (!isBatchEdit) {
            const existingParentProduct = {...parentProduct};
            const existingDesignsMap = {...designsMap};
            const existingCommonSettings = {...commonSettings};

            const currentDesignDetail = existingDesignsMap[existingParentProduct?.id];
            currentDesignDetail.settings = existingCommonSettings;
            prepareStageContainerSize(currentDesignDetail);
            existingDesignsMap[existingParentProduct?.id] = currentDesignDetail;

            setDesignDetail(currentDesignDetail);
            setDesignsMap(existingDesignsMap);
        }
    }

    const updateExistingDesignVariants = async () => {
        if (isBatchEdit && saveDesignVariants) {
            const existingCommonSettings = {...commonSettings};
            const designVariants = _.cloneDeep(existingCommonSettings.designVariants);
            const batchSettings = prepareDesignBatchSettings(existingCommonSettings);

            _.forEach(designVariants, designVariant => {
                designVariant.batchSettings = batchSettings;
                designVariant.settings.background = batchSettings?.background;
                designVariant.settings.noText = true;
            });

            await updateDesignVariants(designVariants);

            const currentExistingDesignDetail = {...existingDesignDetail};
            const batchIndex = _.findIndex(currentExistingDesignDetail.batches, ['batchKey', batchDetail.batchKey]);
            currentExistingDesignDetail.batches[batchIndex] = batchSettings;
            await updateSingleDesign(existingDesignDetail);

            setDesignVariantList(designVariants);
            setExistingDesignDetail(currentExistingDesignDetail);
            setSaveDesignVariants(false);
        }
    }

    const prepareDesignBatchSettings = (existingSettings) => {
        const batchSettings = _.cloneDeep(existingSettings);
        delete batchSettings.designVariants;
        delete batchSettings.images;
        return batchSettings;
    }

    const initCommonSettings = async () => {

        if (isBatchEdit) {
            const currentDesignDetail: any = {};
            // const settings = _.cloneDeep(batchDetail);
            const settings = _.cloneDeep(designVariantList[0]?.batchSettings);
            delete settings.variants;
            settings.designVariants = designVariantList;
            settings.width = settings.width || designVariantList[0]?.batchSettings?.width;
            settings.height = settings.height || designVariantList[0]?.batchSettings?.height;

            currentDesignDetail.settings = settings;

            const initialCommonSettings = currentDesignDetail.settings;
            populatedExistingDesignVariants(initialCommonSettings);
        } else {
            const existingParentProduct = {...parentProduct};
            const existingDesignsMap = {...designsMap};

            const currentDesignDetail = existingDesignsMap[existingParentProduct?.id];
            prepareStageContainerSize(currentDesignDetail);
            setDesignDetail(currentDesignDetail);

            const initialCommonSettings = currentDesignDetail.settings;
            initialCommonSettings.batchKey = currentDesignDetail.settings.batchKey;

            await prepareSelectedImagesForVariant(initialCommonSettings, currentDesignDetail);
        }
    }

    const populatedExistingDesignVariants = (initialCommonSettings) => {
        let existingCommonSettings = initialCommonSettings;

        if (_.isEmpty(initialCommonSettings)) {
            existingCommonSettings = {...commonSettings};
        }

        processSettingsAttributes(existingCommonSettings);
        setCommonSettings(existingCommonSettings);

        setTimeout(() => {
            setShowDesigner(true);
        }, 1000)
    }

    const handleSettingsChanges = async (event?) => {
        const {
            name,
            value,
            type
        } = event.target;
        const existingCommonSettings = {...commonSettings};

        if (type === 'number' && !_.isNaN(value)) {
            existingCommonSettings[name] = _.parseInt(value);
        } else if (type === 'checkbox') {
            existingCommonSettings[name] = !existingCommonSettings[name];
        } else {
            existingCommonSettings[name] = value;
        }

        if (name === 'isRepeatImage') {
            existingCommonSettings.fillPatternRepeat = existingCommonSettings[name] ? 'repeat' : 'no-repeat';
        } else if (name === 'template') {
            await processTemplateImage(existingCommonSettings);
        }

        processSettingsAttributes(existingCommonSettings);
        applySettingsToImages(existingCommonSettings);

        setCommonSettings(existingCommonSettings);
    }

    const processTemplateImage = async (existingCommonSettings) => {
        const currentParentProduct = {...parentProduct};
        const productLayout = currentParentProduct?.layout;
        const templatesMap = _.keyBy(productLayout?.templates, 'template_id');
        const selectedTemplate = templatesMap[existingCommonSettings?.template];

        if (selectedTemplate) {
            const currentTemplateImage: any = {};
            const templateImageDataUrl = await getExternalImageDownloadUrl(selectedTemplate?.image_url, selectedTemplate?.template_id, 'template');
            currentTemplateImage.src = templateImageDataUrl;
            currentTemplateImage.width = selectedTemplate?.print_area_width;
            currentTemplateImage.height = selectedTemplate?.print_area_height;
            currentTemplateImage.opacity = 50;
            currentTemplateImage.fillPatternOffsetX = selectedTemplate?.print_area_left;
            currentTemplateImage.fillPatternOffsetY = selectedTemplate?.print_area_top;
            currentTemplateImage.fillPatternRotation = 0;
            currentTemplateImage.x = selectedTemplate?.print_area_left;
            currentTemplateImage.y = selectedTemplate?.print_area_top;

            setTemplateImage(currentTemplateImage);
            existingCommonSettings.templateImage = currentTemplateImage;
        }
    }

    const processSettingsAttributes = (existingCommonSettings) => {
        if (_.isNaN(existingCommonSettings?.size)) {
            existingCommonSettings.size = 5;
        }

        if (_.isNaN(existingCommonSettings?.fillPatternOffsetXPercentage)) {
            existingCommonSettings.fillPatternOffsetXPercentage = 0;
        }

        if (_.isNaN(existingCommonSettings?.fillPatternOffsetYPercentage)) {
            existingCommonSettings.fillPatternOffsetYPercentage = 0;
        }

        if (_.isNaN(existingCommonSettings?.fillPatternOffsetXValue)) {
            existingCommonSettings.fillPatternOffsetXValue = 0;
        }

        if (_.isNaN(existingCommonSettings?.fillPatternOffsetYValue)) {
            existingCommonSettings.fillPatternOffsetYValue = 0;
        }

        if (_.isNaN(existingCommonSettings?.fillPatternRotation)) {
            existingCommonSettings.fillPatternRotation = 0;
        }
    }

    const applySettingsToImages = (existingCommonSettings) => {
        const existingImagesList = existingCommonSettings.images;
        const existingDesignVariantList = existingCommonSettings.designVariants;

        _.forEach(existingImagesList, imageItem => {
            applySettingsToSingleImage(imageItem, existingCommonSettings);
        });

        _.forEach(existingDesignVariantList, designVariant => {
            applySettingsToSingleImage(designVariant.images[0], existingCommonSettings);
        });

        existingCommonSettings.images = existingImagesList;
        existingCommonSettings.designVariants = existingDesignVariantList;
    }

    const applySettingsToSingleImage = (imageItem, settings) => {
        imageItem.width = imageItem.imageWidth * settings.size / 100;
        imageItem.height = imageItem.imageHeight * settings.size / 100;

        imageItem.fillPatternOffsetX = -imageItem.width * settings.fillPatternOffsetXPercentage / 100;
        imageItem.fillPatternOffsetY = imageItem.height * settings.fillPatternOffsetYPercentage / 100;

        if (_.isNumber(settings.fillPatternOffsetXValue)) {
            imageItem.fillPatternOffsetX += settings.fillPatternOffsetXValue;
        }

        if (_.isNumber(settings.fillPatternOffsetYValue)) {
            imageItem.fillPatternOffsetY += settings.fillPatternOffsetYValue;
        }

        imageItem.fillPatternScaleX = imageItem?.height / settings?.height;
        imageItem.fillPatternScaleY = imageItem?.height / settings?.height;

        imageItem.fillPatternRotation = settings.fillPatternRotation;
        imageItem.fillPatternRepeat = settings.fillPatternRepeat;

        imageItem.isRepeatImage = settings.isRepeatImage;
        imageItem.isBackgroundImage = settings.isBackgroundImage;
        imageItem.size = settings.size;
        imageItem.fillPatternOffsetXPercentage = settings.fillPatternOffsetXPercentage;
        imageItem.fillPatternOffsetYPercentage = settings.fillPatternOffsetYPercentage;
    }

    const prepareSelectedImagesForVariant = async (initialCommonSettings, currentDesignDetail) => {
        let existingCommonSettings = initialCommonSettings;

        if (_.isEmpty(initialCommonSettings)) {
            existingCommonSettings = {...commonSettings};
        }

        processSettingsAttributes(existingCommonSettings);
        existingCommonSettings.images = [];
        existingCommonSettings.designVariants = [];

        _.forEach(processedImageList, processedImage => {
            applySettingsToSingleImage(processedImage, existingCommonSettings);
            existingCommonSettings?.images?.push(processedImage);
            prepareDesignVariant(processedImage, existingCommonSettings, currentDesignDetail);
        });

        setCommonSettings(existingCommonSettings);

        setTimeout(() => {
            setShowDesigner(true);
        }, 1000)
    }

    const prepareDesignVariant = (imageDetail, existingCommonSettings, currentDesignDetail) => {
        const existingVariants = existingCommonSettings?.designVariants || [];
        const designVariant: any = {};
        designVariant.designId = currentDesignDetail?.id;
        designVariant.name = currentDesignDetail?.name;
        designVariant.key = getNextItemKey(currentDesignDetail?.variants || existingVariants, DESIGN_KEY_PREFIX.VARIANT);
        designVariant.status = DESIGN_VARIANT_STATUS.IN_DESIGN;
        designVariant.batchKey = existingCommonSettings.batchKey;
        prepareImageFileName(designVariant, currentDesignDetail);

        if (!existingCommonSettings?.initialSize || _.isNaN(existingCommonSettings?.initialSize) || existingCommonSettings?.initialSize === 0) {

            let minImageSize = imageDetail?.imageWidth;
            if (imageDetail?.imageWidth < imageDetail?.imageHeight) {
                minImageSize = imageDetail?.imageWidth;
            } else if (imageDetail?.imageHeight < imageDetail?.imageWidth) {
                minImageSize = imageDetail?.imageHeight;
            }

            let minSize = existingCommonSettings?.width;
            if (existingCommonSettings?.width < existingCommonSettings?.height) {
                minSize = existingCommonSettings?.width;
            } else if (existingCommonSettings?.height < existingCommonSettings?.width) {
                minSize = existingCommonSettings?.height;
            }

            let initialSize = 100;
            initialSize = ((minSize / minImageSize) * 100) / 2;

            existingCommonSettings.size = _.round(initialSize, 0);
            existingCommonSettings.initialSize = existingCommonSettings.size;
        }

        imageDetail.size = existingCommonSettings.size;

        designVariant.images = [];
        delete imageDetail.key;
        designVariant.images.push(imageDetail);

        existingVariants.push(designVariant);
        existingCommonSettings.designVariants = existingVariants;
    }

    const prepareStageContainerSize = (currentDesignDetail) => {
        const settings = currentDesignDetail.settings;

        const heightRatio = settings.height / 500;
        const widthRatio = settings.width / 500;

        let sizeRatio = heightRatio > widthRatio ? heightRatio : widthRatio;

        const containerWidth = settings.width / sizeRatio;
        const containerHeight = settings.height / sizeRatio;

        currentDesignDetail.settings.stageContainer = {
            width: containerWidth,
            height: containerHeight
        };
    }

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {
                if (downloadAllImages) {
                    let nextIndex = 0;
                    let existingDesignVariants = [...commonSettings?.designVariants];
                    let currentSelectedDesignVariant: any = {...selectedDesignVariant};

                    if (currentSelectedDesignVariant) {
                        const currentIndex = _.findIndex(existingDesignVariants, ['key', currentSelectedDesignVariant?.key]);
                        nextIndex = currentIndex + 1;
                    }

                    if (existingDesignVariants && existingDesignVariants?.hasOwnProperty(nextIndex)) {
                        setSelectedDesignVariant(existingDesignVariants[nextIndex]);
                    } else {
                        setDownloadAllImages(false);
                        setSelectedDesignVariant(null);
                    }
                }

            }, DOWNLOAD_MULTI_IMAGE_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    });

    return (
        <>

            <div className="flex justify-start">

                {selectedParentProductList && selectedParentProductList?.length > 0 &&
                    <div className='w-1/3 mr-3'>
                        <SelectedParentProductsList
                            selectedParentProductList={selectedParentProductList}
                            selectedParentProduct={parentProduct}
                            setSelectedParentProduct={setSelectedParentProduct}/>
                    </div>
                }

                <div className="flex flex-wrap justify-start w-full mx-3">

                    <div
                        className={`${containerCss} overflow-y-scroll w-full border border-gray-300 rounded-b bg-gray-100 flex flex-wrap content-start items-start justify-center`}>

                        <div className="mt-8 flex content-start items-start justify-center">
                            <div>
                                <ShowProgressSpinner dataFetchInProgress={!showDesigner}/>
                            </div>
                        </div>

                        {showDesigner && commonSettings?.stageContainer && commonSettings?.stageContainer?.width > 0 && commonSettings?.width > 0 &&
                            <>

                                {commonSettings?.designVariants?.map((designVariant, index) => (
                                    <React.Fragment key={index}>

                                        <RenderSingleDesignVariant
                                            designVariant={designVariant}
                                            settings={commonSettings}
                                            templateImage={templateImage}
                                            selectedDesignVariant={selectedDesignVariant}
                                            variantIndex={index + 1}
                                            showGrid={showGrid}/>

                                    </React.Fragment>
                                ))}

                            </>
                        }

                    </div>

                </div>

                <div className='w-1/7 ml-3'>
                    <div className="flex flex-wrap justify-start">
                        <ParentProductTemplates
                            parentProduct={parentProduct}
                            templateImage={templateImage}
                            setTemplateImage={setTemplateImage}/>
                    </div>
                </div>

                <div className='w-2/7 ml-3'>

                    {commonSettings?.width > 0 &&
                        <div className={`${containerCss}`}>
                            <div className="mb-2 ml-2 text-lg text-purple-600 font-bold">
                                Design Size (WxH) ({commonSettings?.width} x {commonSettings?.height})
                            </div>

                            <SelectColorTypeDetails
                                label={'Background Color & Shade'}
                                parentForm={commonSettings?.background}
                                colorAttribute={'background'}
                                colorKeyAttribute={'backgroundColorKey'}
                                colorCodeKeyAttribute={'backgroundColorCodeKey'}
                                settings={commonSettings}
                                handleColorDetailsChanges={handleSettingsChanges}
                                handleClearColorDetailsChanges={undefined}/>

                            {/*<div className="flex flex-wrap justify-start">
                                <RenderFields
                                    parentForm={commonSettings}
                                    handleDetailChanges={handleSettingsChanges}
                                    fieldList={_.groupBy(REPEAT_IMAGE_FIELD_LIST, 'group')['template']}
                                    enableEdit={true}
                                    fieldClassName={'w-1/2'}/>
                            </div>*/}

                            <div className="mt-4 ml-2 text-lg text-purple-600">
                                Image Attributes
                            </div>

                            <div className="flex justify-between">

                                <div className="justify-start flex ml-2 mt-4 w-1/2">
                                    <input
                                        type="checkbox"
                                        className="h-6 w-6 mr-1"
                                        id="isRepeatImage"
                                        name="isRepeatImage"
                                        value={commonSettings?.isRepeatImage}
                                        checked={commonSettings?.isRepeatImage}
                                        onChange={handleSettingsChanges}>
                                    </input>
                                    <div className='ml-2'>
                                        <label className='field-label self-center'
                                               htmlFor='isRepeatImage'>Repeat Image</label>
                                    </div>
                                </div>

                            </div>

                            <div className="flex flex-wrap justify-start">
                                <RenderFields
                                    parentForm={commonSettings}
                                    handleDetailChanges={handleSettingsChanges}
                                    fieldList={_.groupBy(REPEAT_IMAGE_FIELD_LIST, 'group')['dimension']}
                                    enableEdit={true}
                                    fieldClassName={'w-1/2'}/>
                            </div>


                            {/*<div className="flex flex-wrap justify-start">
                                <ParentProductTemplates
                                    parentProduct={parentProduct}
                                    templateImage={templateImage}
                                    setTemplateImage={setTemplateImage}/>
                            </div>*/}

                            {/*<div className="flex flex-wrap justify-start">
                                <div
                                    className={`field-container`}>
                                    <div className="field-label w-full">
                                        Layout Template
                                    </div>
                                    <select
                                        className="input-field-max w-full"
                                        id={'template'}
                                        name={'template'}
                                        value={commonSettings?.template || ''}
                                        onChange={handleSettingsChanges}>
                                        <option value="">Choose</option>
                                        {parentProduct?.layout?.templates?.map((layoutTemplate, index) => (
                                            <option key={index}
                                                    value={layoutTemplate?.template_id}>{layoutTemplate?.template_id}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>*/}
                        </div>
                    }

                </div>

            </div>

        </>
    )
}

export default ProductsDesigner
